import React, { useState, useCallback } from 'react';
import type { WidgetConfig } from './types';
import { WidgetAuthProvider, useWidgetAuth } from './WidgetAuthService';
import { WidgetNotificationsProvider, useWidgetNotifications } from './WidgetNotificationService';
import { WidgetButton } from './WidgetButton';
import { WidgetModal } from './WidgetModal';
import { WidgetToast } from './WidgetToast';
import { WidgetChatContent } from './WidgetChatContent';
import './widget.scss';

interface AtlasWidgetProps {
    config: WidgetConfig;
}

/**
 * Main Atlas Widget component
 * Orchestrates the button, modal, toast, and chat functionality
 */
export const AtlasWidget: React.FC<AtlasWidgetProps> = ({ config }) => {
    return (
        <WidgetAuthProvider config={config}>
            <WidgetInner config={config} />
        </WidgetAuthProvider>
    );
};

/**
 * Inner component that wraps notification provider (needs auth context)
 */
const WidgetInner: React.FC<{ config: WidgetConfig }> = ({ config }) => {
    const { isAuthenticated } = useWidgetAuth();

    // Only connect to notifications if authenticated
    if (isAuthenticated) {
        return (
            <WidgetNotificationsProvider config={config}>
                <WidgetContent config={config} />
            </WidgetNotificationsProvider>
        );
    }

    // Show button without notifications when not authenticated
    return <WidgetContent config={config} />;
};

/**
 * Inner widget content that has access to auth and notification contexts
 */
const WidgetContent: React.FC<{ config: WidgetConfig }> = ({ config }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasNewNotification, setHasNewNotification] = useState(false);

    const { isAuthenticated, login, isLoading } = useWidgetAuth();
    const notifications = useWidgetNotifications();

    // Get notification values (defaults if not available)
    const unreadCount = notifications?.unreadCount ?? 0;
    const latestNotification = notifications?.latestNotification ?? null;
    const clearLatestNotification = notifications?.clearLatestNotification ?? (() => { });

    const handleButtonClick = useCallback(() => {
        if (!isAuthenticated && !isLoading) {
            // Not authenticated - trigger login
            login();
            return;
        }
        setIsModalOpen(true);
        setHasNewNotification(false);
    }, [isAuthenticated, isLoading, login]);

    const handleModalClose = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleToastDismiss = useCallback(() => {
        clearLatestNotification();
    }, [clearLatestNotification]);

    const handleToastClick = useCallback(() => {
        setIsModalOpen(true);
        clearLatestNotification();
    }, [clearLatestNotification]);

    // Set pulse animation when new notification arrives
    React.useEffect(() => {
        if (latestNotification && !isModalOpen) {
            setHasNewNotification(true);
            const timer = setTimeout(() => {
                setHasNewNotification(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [latestNotification, isModalOpen]);

    return (
        <>
            {/* Floating Button */}
            <WidgetButton
                position={config.position}
                unreadCount={unreadCount}
                onClick={handleButtonClick}
                hasNewNotification={hasNewNotification}
            />

            {/* Toast Notification */}
            {!isModalOpen && latestNotification && (
                <WidgetToast
                    notification={latestNotification}
                    overlayPosition={config.overlayPosition}
                    onDismiss={handleToastDismiss}
                    onClick={handleToastClick}
                />
            )}

            {/* Modal with Chat */}
            <WidgetModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                overlayPosition={config.overlayPosition}
            >
                <WidgetChatContent config={config} />
            </WidgetModal>
        </>
    );
};
